import { html, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import EkoElement from "@/utilities/element";
import style from "./banner.css?inline";
import "./banner-light-dom.css";
import "@/components/button";
import "@/components/featured-icon";

export const themes = ["accent", "default"] as const;
export type BannerTheme = (typeof themes)[number];

export const variants = ["default", "floating"] as const;
export type BannerVariant = (typeof variants)[number];

export class BannerDismissEvent extends CustomEvent<void> {
  constructor() {
    super("dismiss", { bubbles: true, composed: true });
  }
}

@customElement("eko-banner")
class Banner extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  @property({ type: Boolean }) dismissible = false;
  @property({ reflect: true, type: Boolean }) exiting = false;
  @property({ reflect: true }) icon: string = "";
  @property({ reflect: true }) theme: BannerTheme = "default";
  @property({ reflect: true }) variant: BannerVariant = "default";

  private dismiss() {
    if (this.exiting) return;

    this.exiting = true;

    this.addEventListener(
      "animationend",
      () => {
        this.dispatchEvent(new BannerDismissEvent());
      },
      { once: true }
    );
  }

  render() {
    return html`
      ${this.icon
        ? html`<eko-featured-icon
            icon=${this.icon}
            part="icon"
            size="sm"
            theme=${ifDefined(this.theme === "accent" ? "accent" : undefined)}
            variant=${ifDefined(
              this.theme === "accent" ? "square-dark" : undefined
            )}></eko-featured-icon>`
        : nothing}
      <span class="eko-banner__content" part="content">
        <slot></slot>
        <span class="eko-banner__append" part="append">
          <slot name="append"></slot>
        </span>
      </span>
      <span class="eko-banner__actions" part="actions">
        <slot name="actions"></slot>
      </span>
      ${this.dismissible
        ? html`<eko-button
            class="eko-banner__close"
            variant="plain"
            square
            @click=${this.dismiss}
            part="close">
            <eko-icon name="x-close" part="close-icon"></eko-icon>
          </eko-button>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-banner": Banner;
  }
}

export default Banner;
